/**
 * ConfigHealthAdmin - Health check de configuración por país
 *
 * Detecta qué variables de entorno faltan para cada país (Argentina vs Internacional)
 * y señala las variables de entorno faltantes en Railway.
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Flag,
  Globe,
  Info,
  RefreshCw,
  Settings,
  XCircle,
} from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useCallback, useEffect, useRef, useState } from "react";

interface ConfigIssue {
  severity: "error" | "warning" | "info";
  category: "payment" | "pricing" | "assets" | "analytics" | "worker" | "brand" | "database" | "auth" | "media" | "email" | "notification";
  key: string;
  description: string;
  recommendation: string;
  affectedCountries: string[];
}

interface CountryConfigStatus {
  country: string;
  countryName: string;
  overallStatus: "complete" | "incomplete" | "critical";
  completeness: number;
  issues: ConfigIssue[];
}

interface ConfigHealthResponse {
  timestamp: string;
  overall_status: "healthy" | "degraded" | "critical";
  countries: CountryConfigStatus[];
  shared_config: {
    status: "complete" | "incomplete";
    issues: ConfigIssue[];
  };
  missing_count: {
    critical: number;
    warnings: number;
    total: number;
  };
}

function SeverityBadge({ severity }: { severity: ConfigIssue["severity"] }) {
  const config = {
    error: { label: "Crítico", variant: "destructive" as const, icon: XCircle },
    warning: { label: "Advertencia", variant: "default" as const, icon: AlertTriangle },
    info: { label: "Info", variant: "secondary" as const, icon: Info },
  };

  const { label, variant, icon: Icon } = config[severity];

  return (
    <Badge variant={variant} className="gap-1">
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

function CategoryBadge({ category }: { category: ConfigIssue["category"] }) {
  const config: Record<ConfigIssue["category"], { label: string; color: string }> = {
    payment:      { label: "Pagos",        color: "bg-emerald-500/10 text-emerald-600" },
    pricing:      { label: "Precios",      color: "bg-blue-500/10 text-blue-600"     },
    assets:       { label: "Assets",       color: "bg-purple-500/10 text-purple-600" },
    analytics:    { label: "Analytics",    color: "bg-amber-500/10 text-amber-600"   },
    worker:       { label: "Worker",       color: "bg-cyan-500/10 text-cyan-600"     },
    brand:        { label: "Brand",        color: "bg-orange-500/10 text-orange-600" },
    database:     { label: "Base de datos",color: "bg-zinc-500/10 text-zinc-400"     },
    auth:         { label: "Auth",         color: "bg-indigo-500/10 text-indigo-600" },
    media:        { label: "Media",        color: "bg-pink-500/10 text-pink-600"     },
    email:        { label: "Email",        color: "bg-teal-500/10 text-teal-600"     },
    notification: { label: "Notificación", color: "bg-yellow-500/10 text-yellow-600" },
  };

  const { label, color } = config[category] ?? { label: category, color: "bg-zinc-500/10 text-zinc-400" };

  return (
    <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

function StatusIcon({ status }: { status: "complete" | "incomplete" | "critical" }) {
  if (status === "complete") {
    return <CheckCircle2 className="h-5 w-5 text-green-500" />;
  }
  if (status === "critical") {
    return <XCircle className="h-5 w-5 text-destructive" />;
  }
  return <AlertTriangle className="h-5 w-5 text-amber-500" />;
}

function IssueCard({ issue }: { issue: ConfigIssue }) {
  return (
    <Card className="border-l-4 border-l-border/50">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <CategoryBadge category={issue.category} />
              <SeverityBadge severity={issue.severity} />
            </div>
            <CardTitle className="text-base leading-tight">
              <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono">{issue.key}</code>
            </CardTitle>
          </div>
        </div>
        <CardDescription className="mt-2">{issue.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="rounded-md bg-muted/50 p-3">
          <p className="text-sm font-medium text-foreground/90">Recomendación:</p>
          <p className="mt-1 text-sm text-muted-foreground">{issue.recommendation}</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Globe className="h-3 w-3" />
          <span>Afecta a: {issue.affectedCountries.join(", ")}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function CountryStatusCard({ country }: { country: CountryConfigStatus }) {
  const isArgentina = country.country === "AR";

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isArgentina ? (
              <Flag className="h-6 w-6 text-blue-500" />
            ) : (
              <Globe className="h-6 w-6 text-primary" />
            )}
            <div>
              <CardTitle className="text-lg">{country.countryName}</CardTitle>
              <CardDescription className="text-xs">
                Stripe (USD) — todos los países
              </CardDescription>
            </div>
          </div>
          <div className="text-right">
            <StatusIcon status={country.overallStatus} />
            <p className="mt-1 text-sm font-semibold text-muted-foreground">{country.completeness}%</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-4">
          {country.issues.length === 0 ? (
            <Alert className="border-green-500/50 bg-green-500/5">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <AlertTitle className="text-green-600">Configuración completa</AlertTitle>
              <AlertDescription className="text-green-600/80">
                Todas las variables de entorno necesarias están configuradas para {country.countryName}.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
                <span className="text-sm font-medium">Issues detectados</span>
                <Badge variant="outline">{country.issues.length}</Badge>
              </div>
              <div className="space-y-3">
                {country.issues.map((issue, idx) => (
                  <IssueCard key={`${issue.key}-${idx}`} issue={issue} />
                ))}
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const CONFIG_HEALTH_POLL_MS = 5_000;

// Build a deterministic signature from missing keys + counts so we can flash on change
function buildConfigSignature(r: ConfigHealthResponse): string {
  const allKeys = [
    ...r.countries.flatMap((c) => c.issues.map((i) => `${c.country}:${i.severity}:${i.key}`)),
    ...r.shared_config.issues.map((i) => `shared:${i.severity}:${i.key}`),
  ].sort();
  return JSON.stringify({
    overall: r.overall_status,
    critical: r.missing_count.critical,
    warnings: r.missing_count.warnings,
    total: r.missing_count.total,
    keys: allKeys,
  });
}

export default function ConfigHealthAdmin() {
  const { getAuthHeaders } = useAdminAuth();
  const [data, setData] = useState<ConfigHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [secsSince, setSecsSince] = useState(0);
  const [changesDetected, setChangesDetected] = useState(0);
  const [flashUntil, setFlashUntil] = useState(0);
  const prevSignatureRef = useRef<string | null>(null);
  const getAuthHeadersRef = useRef(getAuthHeaders);
  getAuthHeadersRef.current = getAuthHeaders;

  const fetchHealth = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const headers = await getAuthHeadersRef.current();
      const res = await fetch("/api/admin/config-health", { headers });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(
          `HTTP ${res.status}: ${res.statusText}${body ? ` — ${body.slice(0, 200)}` : ""}`,
        );
      }
      const json = (await res.json()) as ConfigHealthResponse;
      // Change detection
      const sig = buildConfigSignature(json);
      if (prevSignatureRef.current !== null && prevSignatureRef.current !== sig) {
        setChangesDetected((n) => n + 1);
        setFlashUntil(Date.now() + 2500);
      }
      prevSignatureRef.current = sig;
      setData(json);
      setLastUpdated(new Date());
      if (silent) setError(null);
    } catch (err) {
      if (!silent) {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchHealth();
  }, [fetchHealth]);

  useEffect(() => {
    const id = window.setInterval(() => {
      void fetchHealth({ silent: true });
    }, CONFIG_HEALTH_POLL_MS);
    return () => window.clearInterval(id);
  }, [fetchHealth]);

  // Live seconds-since counter — resets whenever lastUpdated changes
  useEffect(() => {
    setSecsSince(0);
    const id = window.setInterval(() => setSecsSince((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [lastUpdated]);

  // Force re-render when flash expires
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (flashUntil <= Date.now()) return;
    const id = window.setTimeout(() => forceTick((n) => n + 1), flashUntil - Date.now());
    return () => window.clearTimeout(id);
  }, [flashUntil]);

  const isFlashing = Date.now() < flashUntil;

  return (
    <div className="mx-auto max-w-7xl space-y-8">
      <AdminPageHeader description="Gaps de producto por país/template. Ping de APIs → Integraciones. Env crudas → Env vars." />

      {/* Header con status general */}
      {!loading && data && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Status General</CardDescription>
              <CardTitle className="flex items-center gap-2 text-2xl">
                {data.overall_status === "healthy" && (
                  <>
                    <CheckCircle2 className="h-6 w-6 text-green-500" />
                    <span className="text-green-600">Saludable</span>
                  </>
                )}
                {data.overall_status === "degraded" && (
                  <>
                    <AlertTriangle className="h-6 w-6 text-amber-500" />
                    <span className="text-amber-600">Degradado</span>
                  </>
                )}
                {data.overall_status === "critical" && (
                  <>
                    <XCircle className="h-6 w-6 text-destructive" />
                    <span className="text-destructive">Crítico</span>
                  </>
                )}
              </CardTitle>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Issues Críticos</CardDescription>
              <CardTitle className="text-2xl">
                {data.missing_count.critical === 0 ? (
                  <span className="text-green-600">0</span>
                ) : (
                  <span className="text-destructive">{data.missing_count.critical}</span>
                )}
              </CardTitle>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Advertencias</CardDescription>
              <CardTitle className="text-2xl">
                {data.missing_count.warnings === 0 ? (
                  <span className="text-green-600">0</span>
                ) : (
                  <span className="text-amber-600">{data.missing_count.warnings}</span>
                )}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* Botones de acción */}
      <div className={`flex flex-wrap items-center gap-3 transition-all duration-500 ${isFlashing ? "rounded-md ring-2 ring-amber-400/50 px-2 py-1" : ""}`}>
        {/* Live indicator */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="relative flex h-2 w-2">
            <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${isFlashing ? "bg-amber-400" : "bg-emerald-400"}`} />
            <span className={`relative inline-flex h-2 w-2 rounded-full ${isFlashing ? "bg-amber-500" : "bg-emerald-500"}`} />
          </span>
          <span>
            En vivo · cada {CONFIG_HEALTH_POLL_MS / 1000}s
            {lastUpdated ? (
              <> · verificado hace <strong className="tabular-nums">{secsSince}s</strong></>
            ) : (
              <> · cargando…</>
            )}
            {changesDetected > 0 && (
              <> · <span className="font-semibold text-amber-600">{changesDetected} cambio{changesDetected === 1 ? "" : "s"}</span></>
            )}
          </span>
        </div>
        <Button onClick={() => void fetchHealth()} disabled={loading} variant="outline" size="sm" type="button">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Actualizar ahora
        </Button>
        <Button asChild variant="outline" size="sm">
          <a href="https://railway.com/dashboard" target="_blank" rel="noopener noreferrer">
            <Settings className="mr-2 h-4 w-4" />
            Ir a Railway
            <ExternalLink className="ml-2 h-3 w-3" />
          </a>
        </Button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Error al cargar health check</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Content */}
      {!loading && !error && data && (
        <Tabs defaultValue="all" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="all" className="gap-2">
              <AlertTriangle className="h-4 w-4" />
              Todo ({data.missing_count.total})
            </TabsTrigger>
            <TabsTrigger value="argentina" className="gap-2">
              <Flag className="h-4 w-4" />
              Argentina
              {data.countries[0]?.overallStatus === "critical" && (
                <Badge variant="destructive" className="ml-2 h-5 w-5 rounded-full p-0">
                  !
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="international" className="gap-2">
              <Globe className="h-4 w-4" />
              Internacional
              {data.countries[1]?.overallStatus === "critical" && (
                <Badge variant="destructive" className="ml-2 h-5 w-5 rounded-full p-0">
                  !
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="shared" className="gap-2">
              <Settings className="h-4 w-4" />
              Compartido
              {data.shared_config.status === "incomplete" && (
                <Badge variant="outline" className="ml-2 h-5 w-5 rounded-full p-0">
                  !
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4">
            {data.missing_count.total === 0 ? (
              <Alert className="border-green-500/50 bg-green-500/5">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <AlertTitle className="text-green-600">Todo configurado correctamente</AlertTitle>
                <AlertDescription className="text-green-600/80">
                  No se detectaron variables de entorno faltantes.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-3">
                {[
                  ...data.countries.flatMap((c) => c.issues),
                  ...data.shared_config.issues,
                ]
                  .sort((a, b) => {
                    const rank = { error: 0, warning: 1, info: 2 };
                    return rank[a.severity] - rank[b.severity];
                  })
                  .map((issue, idx) => (
                    <IssueCard key={`all-${issue.key}-${idx}`} issue={issue} />
                  ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="argentina" className="space-y-6">
            <CountryStatusCard country={data.countries[0]} />
          </TabsContent>

          <TabsContent value="international" className="space-y-6">
            <CountryStatusCard country={data.countries[1]} />
          </TabsContent>

          <TabsContent value="shared" className="space-y-6">
            <Card>
              <CardHeader className="bg-muted/30">
                <div className="flex items-center gap-3">
                  <Settings className="h-6 w-6 text-primary" />
                  <div>
                    <CardTitle className="text-lg">Configuración Compartida</CardTitle>
                    <CardDescription className="text-xs">
                      Variables de entorno que afectan a todos los países
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {data.shared_config.issues.length === 0 ? (
                    <Alert className="border-green-500/50 bg-green-500/5">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <AlertTitle className="text-green-600">Configuración completa</AlertTitle>
                      <AlertDescription className="text-green-600/80">
                        Todas las variables compartidas están configuradas correctamente.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
                        <span className="text-sm font-medium">Issues detectados</span>
                        <Badge variant="outline">{data.shared_config.issues.length}</Badge>
                      </div>
                      <div className="space-y-3">
                        {data.shared_config.issues.map((issue, idx) => (
                          <IssueCard key={`${issue.key}-${idx}`} issue={issue} />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Footer info */}
      <Alert className="border-border/80 bg-muted/40">
        <Info className="h-4 w-4" />
        <AlertTitle>Cómo actualizar variables de entorno</AlertTitle>
        <AlertDescription className="space-y-2 text-muted-foreground">
          <p>
            1. Ir a{" "}
            <a
              href="https://railway.com/dashboard"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Railway Dashboard
            </a>
          </p>
          <p>2. Proyecto → servicio <code className="rounded bg-background px-1">anaafernandez-web</code> (y webhook si aplica) → Variables</p>
          <p>3. Agregar o actualizar las variables faltantes</p>
          <p>4. Redeploy automático al guardar, o push a <code className="rounded bg-background px-1">main</code></p>
          <p className="mt-3 text-xs">
            <strong>Nota:</strong> Variables con prefijo <code className="rounded bg-background px-1">VITE_</code>{" "}
            son para el frontend (build-time). Variables sin prefijo son para el backend (runtime).
          </p>
        </AlertDescription>
      </Alert>
    </div>
  );
}
